/* Quedarse con el ARCHIVO, no con la referencia al archivo.
 *
 * ── DE DÓNDE SALIÓ ────────────────────────────────────────────────────────
 *
 * Aldo, 29/09: «no me deja cargar la planilla de movimientos cuando la subo».
 * El análisis había andado —el servidor lo contestó en 4,6 s y sin error— y el
 * botón estaba habilitado. Apretaba Cargar y el pedido NUNCA llegaba: en los
 * registros del servidor hay cinco canjes de sesión seguidos, uno por cada
 * clic, y detrás de cada uno nada. La red estaba bien (el canje de 300 ms
 * antes contestó 200 y el resto de la pantalla siguió andando toda la tarde),
 * así que lo que fallaba era leer el archivo para armar el pedido.
 *
 * ── POR QUÉ PASA ──────────────────────────────────────────────────────────
 *
 * Las dos pantallas de subida trabajan en dos pasos —«mirá qué harías» y
 * después «hacelo»— y el archivo viaja en LOS DOS, porque el servidor relee
 * los valores del Excel y de la pantalla salen decisiones, nunca datos. Eso
 * está bien y no se toca.
 *
 * Lo que estaba mal es qué se guardaba en el medio: un `File`, que no es el
 * archivo sino un puntero a un archivo del disco. Entre un paso y el otro
 * pasan minutos —Aldo estuvo cuatro— y en ese rato el Excel se abre, se
 * guarda, lo sincroniza la red o se mueve de carpeta. Ahí el puntero queda
 * muerto: el navegador tira `NotReadableError` al ir a leerlo y el `fetch` se
 * cae ANTES de mandar nada. No hay pedido, no hay estado HTTP, no hay nada que
 * mirar del lado del servidor — que es exactamente lo que se vio.
 *
 * ── LA SOLUCIÓN, Y POR QUÉ ACÁ ────────────────────────────────────────────
 *
 * Se leen los bytes UNA vez, cuando la persona elige el archivo, y se guarda
 * esa copia. Después el disco puede hacer lo que quiera. Una planilla de
 * movimientos pesa 650 KB y la de cargas 450 KB: tenerlas en memoria mientras
 * dura la pantalla no cuesta nada al lado de dejar a alguien sin poder cargar.
 *
 * Y se lee AL ELEGIR y no al confirmar a propósito: si el archivo no se puede
 * leer, que se sepa en el momento en que se lo eligió —cuando la persona
 * todavía está mirando el explorador y puede elegir otro— y no diez minutos
 * después, con la lista en pantalla y el trabajo de decidir ya hecho.
 *
 * Sale un `File` y no un `Blob` para que el resto no cambie: el nombre se
 * muestra en pantalla y el servidor lo mira para rechazar lo que no es .xlsx.
 */

/* La lectura falló: el archivo se movió, se volvió a guardar o ya no está.
 * Es una clase aparte y no un `Error` pelado porque quien llama tiene que
 * poder distinguirla de un problema de red —son dos cosas muy distintas para
 * el que está del otro lado— sin leer el texto del mensaje. */
export class ArchivoIlegible extends Error {
  constructor(causa) {
    super("No se pudo leer el archivo elegido.");
    this.name = "ArchivoIlegible";
    this.causa = causa;
  }
}

/* El texto que se le muestra a la persona. Dice qué pasó y qué hacer; no
 * nombra `File`, ni el navegador, ni el disco. */
export const PORQUE_NO_SE_PUDO_LEER =
  "No pude leer el archivo. Suele pasar cuando el Excel está abierto, se " +
  "guardó o se movió de carpeta después de elegirlo. Cerralo, volvé a " +
  "elegirlo y probá de nuevo.";

/* Los bytes del archivo, en una copia que ya no depende del disco.
 *
 * Tira `ArchivoIlegible` si no se pudo leer. No devuelve `null` en ese caso:
 * un `null` se propaga callado y termina subiendo un archivo vacío, que es
 * peor que no subir nada. */
export async function retener(f) {
  if (!f) throw new ArchivoIlegible(new Error("no vino ningún archivo"));
  let bytes;
  try {
    bytes = await bytesDe(f);
  } catch (e) {
    throw new ArchivoIlegible(e);
  }
  return new File([bytes], f.name, {
    type: f.type,
    lastModified: f.lastModified,
  });
}

/* Los bytes, por el camino que haya.
 *
 * `Blob.arrayBuffer()` es lo corto y es lo que usan los navegadores de la
 * empresa, pero no está en todos lados: el jsdom con el que corren los tests
 * no lo implementa, y tampoco lo tienen los navegadores viejos. Sin el
 * respaldo, esto se cae con «no es una función» —que no es «no se pudo leer el
 * archivo» y mandaría a la persona a cerrar un Excel que no tiene la culpa.
 *
 * `FileReader` está desde siempre y hace lo mismo. Se prueba el corto primero
 * porque no arma un objeto ni escucha eventos. */
function bytesDe(f) {
  if (typeof f.arrayBuffer === "function") return f.arrayBuffer();
  return new Promise((listo, falla) => {
    const fr = new FileReader();
    fr.onload = () => listo(fr.result);
    fr.onerror = () => falla(fr.error || new Error("no se pudo leer"));
    fr.readAsArrayBuffer(f);
  });
}
